import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Container, Table, Button, Row, Col, OverlayTrigger, Popover, InputGroup, Spinner, Modal } from 'react-bootstrap';
import axiosInstance from '../api/axiosInstance';
import { ClipLoader } from "react-spinners";
import { FaBuilding, FaImages, FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaStreetView, FaSearch, FaRedo, FaCheck, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { format } from 'date-fns';

// 코드 매핑 객체
const tradeTypeMap = {
    'A1': '매매',
    'B1': '전세',
    'B2': '월세',
    'B3': '단기임대'
};

const tradeTypeColorMap = {
    'A1': '#d9534f', // 매매 (Red)
    'B1': '#0275d8', // 전세 (Blue)
    'B2': '#ff9800', // 월세 (Orange)
    'B3': '#5bc0de', // 단기임대 (Cyan)
    'S': '#d9534f',  // 과거 실거래가 매매
    'J': '#0275d8',  // 과거 실거래가 전세
    'R': '#ff9800'   // 과거 실거래가 월세
};

const estateTypeMap = {
    'A01': '아파트',
    'A02': '아파트분양권',
    'C01': '오피스텔',
    'C02': '빌라/연립',
    'A06': '단독/다가구',
    'A05': '전원주택',
    'C03': '원룸',
    'D01': '상가',
    'D03': '상가주택',
    'D02': '사무실',
    'D04': '공장/창고',
    'D05': '건물',
    'E04': '지식산업센터'
};

const pricePresets = [
    1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000,
    10000, 20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000,
    100000, 150000, 200000
];

const areaPresets = [33, 66, 99, 132, 165, 198, 231, 264];

// 건축물 정보 한글 라벨 매핑 (일반 사용자용 정제 버전)
const krasLabelMap = {
    rnum: '순번', platPlc: '대지위치', jibun: '번지',
    mgmUpBldrgstPk: '관리상위건축물대장PK', crtnDay: '생성일자',
    regstrGbCdNm: '대장구분코드명', regstrKindCdNm: '대장종류코드명',
    bldNm: '건물명', splotNm: '특수지명', block: '블록', lot: '로트',
    bylotCnt: '외필지수',
    naJibun: '새주소번지',
    jiyukCdNm: '지역코드명', jiguCdNm: '지구코드명', guyukCdNm: '구역코드명',
    dongNm: '동명칭', platArea: '대지면적(㎡)', archArea: '건축면적(㎡)',
    bcRat: '건폐율(%)', totArea: '연면적(㎡)', vlRatEstmTotArea: '용적률산정연면적(㎡)',
    vlRat: '용적률(%)', strctCdNm: '주구조명', etcStrct: '기타구조',
    mainPurpsCdNm: '주용도명', etcPurps: '기타용도', hhldCnt: '세대수',
    fmlyCnt: '가구수', mainAtchGbCdNm: '주부속구분코드명',
    atchBldCnt: '부속건축물수', atchBldArea: '부속건축물면적(㎡)', totDongCnt: '총동수',
    pmsDay: '허가일자', stcnsDay: '착공일자', useAprDay: '사용승인일자',
    rserthqkDsgnApplyYn: '내진설계여부', rserthqkAbil: '내진능력',
    jijiguGbCdNm: '지역지구구역구분', jijiguCdNm: '지역지구구역명',
    etcJijigu: '기타지역지구', reprYn: '대표여부',
    flrGbCdNm: '층구분코드명', flrNoNm: '층번호명',
    area: '면적(㎡)', hsprc: '주택가격', stdDay: '기준일자', hoNm: '호명칭',
    exposPubuseGbCdNm: '전유공용구분코드명',
    excluArea: '전유면적(㎡)', commonArea: '공용면적(㎡)',
    unitGbCdNm: '변기/정화조구분', capa: '용량', newPlatPlc: '도로명대지위치',
    naRoadNm: '도로명', naBldlsHhldNm: '도로본번', naBldlsDongNm: '도로부번'
};

const cpIconMap = {
    'asil': '/images/cp/asil.png',
    'bizmk': '/images/cp/bizmk.png',
    'fine': '/images/cp/fine.png',
    'gongsilclub': '/images/cp/gongsilclub.png',
    'hkdotcom': '/images/cp/hkdotcom.png',
    'industry': '/images/cp/industry.png',
    'kar': '/images/cp/kar.png',
    'neonet': '/images/cp/neonet.png',
    'r114': '/images/cp/r114.png',
    'rets': '/images/cp/rets.png',
    'rter': '/images/cp/rter.png',
    'serve': '/images/cp/serve.png',
    'sunbang': '/images/cp/sunbang.png',
    'ten': '/images/cp/ten.png',
    'thebiz': '/images/cp/thebiz.png',
    'toad': '/images/cp/toad.png',
    'woori': '/images/cp/woori.png'
};

const regionData = [
    {
        name: '동작구', code: '11590',
        dongs: [
            { name: '신대방동', code: '1159010900' },
            { name: '상도동', code: '1159010200' },
            { name: '대방동', code: '1159010800' },
            { name: '사당동', code: '1159010700' }
        ]
    },
    {
        name: '마포구', code: '11440',
        dongs: [
            { name: '성산동', code: '1144012500' },
            { name: '망원동', code: '1144012300' },
            { name: '상암동', code: '1144012700' }
        ]
    },
    {
        name: '서초구', code: '11650',
        dongs: [
            { name: '서초동', code: '1165010800' },
            { name: '방배동', code: '1165010100' }
        ]
    },
    {
        name: '은평구', code: '11380',
        dongs: [
            { name: '증산동', code: '1138011000' },
            { name: '수색동', code: '1138010100' }
        ]
    },
    {
        name: '서대문구', code: '11410',
        dongs: [
            { name: '북가좌동', code: '1141011900' }
        ]
    },
    {
        name: '영등포구', code: '11560',
        dongs: [
            { name: '신길동', code: '1156013200' }
        ]
    },
    {
        name: '노원구', code: '11350',
        dongs: [
            { name: '상계동', code: '1135010500' },
            { name: '중계동', code: '1135010600' },
            { name: '하계동', code: '1135010400' }
        ]
    },
    {
        name: '성동구', code: '11200',
        dongs: [
            { name: '성수동1가', code: '1120011400' },
            { name: '성수동2가', code: '1120011500' }
        ]
    },
    {
        name: '광진구', code: '11215',
        dongs: [
            { name: '화양동', code: '1121510700' },
            { name: '자양동', code: '1121510500' }
        ]
    }
];

// 기존 단일 맵도 호환성을 위해 유지 (테이블 표시용)
const cortarNoMap = regionData.reduce((acc, gu) => {
    acc[gu.code] = gu.name;
    gu.dongs.forEach(dong => {
        acc[dong.code] = dong.name;
    });
    return acc;
}, {});

// 최적화를 위한 개별 매물 행 컴포넌트 (Memoized)
const EstateRow = React.memo(({ item, isSelected, onSelect, cortarNoMap, estateTypeMap, tradeTypeMap, m2toPy }) => {
    const tradeColor = tradeTypeColorMap[item.trade_type] || '#666';
    return (
        <tr
            onClick={() => onSelect(item)}
            style={{ cursor: 'pointer' }}
            className={isSelected ? 'table-warning' : ''}
        >
            <td>{cortarNoMap[item.cortar_no] || item.cortar_no || '-'}</td>
            <td>{item.article_name}</td>
            <td>{estateTypeMap[item.real_estate_type] || item.real_estate_type}</td>
            <td className="fw-bold">
                <span className="trade-type-badge" style={{ backgroundColor: tradeColor + '12', color: tradeColor, border: `1.5px solid ${tradeColor}` }}>
                    {tradeTypeMap[item.trade_type] || item.trade_type}
                </span>
            </td>
            <td className="fw-bold text-primary">
                {item.trade_type === 'B2' && item.rent_price
                    ? `${item.price_text} / ${item.rent_price.toLocaleString()}`
                    : item.price_text}
            </td>
            <td>{item.exclusive_area}㎡{m2toPy(item.exclusive_area)}</td>
            <td>{item.floor_info}</td>
            <td>{item.confirm_date}</td>
        </tr>
    );
});

// --- 동작구 전역 정비사업 구역 데이터 ---
const DONGJAK_DEVELOPMENTS = {
    // 노량진 뉴타운
    noryangjin1: { category: 'newtown', group: '노량진뉴타운', label: '노량진 1구역', center: { lat: 37.5113, lng: 126.9351 }, color: '#673AB7', status: '사업시행인가', path: [{ lat: 37.5135, lng: 126.933 }, { lat: 37.5135, lng: 126.938 }, { lat: 37.509, lng: 126.938 }, { lat: 37.509, lng: 126.933 }] },
    noryangjin2: { category: 'newtown', group: '노량진뉴타운', label: '노량진 2구역', center: { lat: 37.5126, lng: 126.9381 }, color: '#673AB7', status: '착공', path: [{ lat: 37.514, lng: 126.937 }, { lat: 37.514, lng: 126.940 }, { lat: 37.511, lng: 126.940 }, { lat: 37.511, lng: 126.937 }] },
    noryangjin3: { category: 'newtown', group: '노량진뉴타운', label: '노량진 3구역', center: { lat: 37.5135, lng: 126.9412 }, color: '#673AB7', status: '관리처분인가', path: [{ lat: 37.515, lng: 126.940 }, { lat: 37.515, lng: 126.943 }, { lat: 37.512, lng: 126.943 }, { lat: 37.512, lng: 126.940 }] },
    noryangjin4: { category: 'newtown', group: '노량진뉴타운', label: '노량진 4구역', center: { lat: 37.5105, lng: 126.9423 }, color: '#673AB7', status: '이주/철거', path: [{ lat: 37.512, lng: 126.941 }, { lat: 37.512, lng: 126.944 }, { lat: 37.509, lng: 126.944 }, { lat: 37.509, lng: 126.941 }] },
    noryangjin5: { category: 'newtown', group: '노량진뉴타운', label: '노량진 5구역', center: { lat: 37.5091, lng: 126.9395 }, color: '#673AB7', status: '이주중', path: [{ lat: 37.511, lng: 126.938 }, { lat: 37.511, lng: 126.941 }, { lat: 37.508, lng: 126.941 }, { lat: 37.508, lng: 126.938 }] },
    noryangjin6: { category: 'newtown', group: '노량진뉴타운', label: '노량진 6구역', center: { lat: 37.5072, lng: 126.9382 }, color: '#673AB7', status: '착공', path: [{ lat: 37.509, lng: 126.937 }, { lat: 37.509, lng: 126.940 }, { lat: 37.505, lng: 126.940 }, { lat: 37.505, lng: 126.937 }] },
    noryangjin7: { category: 'newtown', group: '노량진뉴타운', label: '노량진 7구역', center: { lat: 37.5065, lng: 126.9341 }, color: '#673AB7', status: '이주중', path: [{ lat: 37.508, lng: 126.933 }, { lat: 37.508, lng: 126.936 }, { lat: 37.505, lng: 126.936 }, { lat: 37.505, lng: 126.933 }] },
    noryangjin8: { category: 'newtown', group: '노량진뉴타운', label: '노량진 8구역', center: { lat: 37.5102, lng: 126.9325 }, color: '#673AB7', status: '착공', path: [{ lat: 37.512, lng: 126.931 }, { lat: 37.512, lng: 126.934 }, { lat: 37.509, lng: 126.934 }, { lat: 37.509, lng: 126.931 }] },

    // 흑석 뉴타운
    heukseok1: { category: 'newtown', group: '흑석뉴타운', label: '흑석 1구역', center: { lat: 37.5085, lng: 126.9632 }, color: '#3F51B5', status: '조합설립인가', path: [{ lat: 37.510, lng: 126.962 }, { lat: 37.510, lng: 126.965 }, { lat: 37.507, lng: 126.965 }, { lat: 37.507, lng: 126.962 }] },
    heukseok2: { category: 'newtown', group: '흑석뉴타운', label: '흑석 2구역', center: { lat: 37.5098, lng: 126.9615 }, color: '#3F51B5', status: '공공재개발 추진중', path: [{ lat: 37.511, lng: 126.960 }, { lat: 37.511, lng: 126.963 }, { lat: 37.508, lng: 126.963 }, { lat: 37.508, lng: 126.960 }] },
    heukseok9: { category: 'newtown', group: '흑석뉴타운', label: '흑석 9구역', center: { lat: 37.5075, lng: 126.9605 }, color: '#3F51B5', status: '이주/철거', path: [{ lat: 37.509, lng: 126.959 }, { lat: 37.509, lng: 126.962 }, { lat: 37.506, lng: 126.962 }, { lat: 37.506, lng: 126.959 }] },
    heukseok11: { category: 'newtown', group: '흑석뉴타운', label: '흑석 11구역', center: { lat: 37.5052, lng: 126.9661 }, color: '#3F51B5', status: '이주/철거완료', path: [{ lat: 37.507, lng: 126.965 }, { lat: 37.507, lng: 126.968 }, { lat: 37.504, lng: 126.968 }, { lat: 37.504, lng: 126.965 }] },

    // 신속통합기획 및 주요 재개발
    sangdo14: {
        category: 'shintong', group: '신속통합/재개발', label: '상도 14구역', status: '정비구역지정', color: '#FF5722',
        path: [{ lat: 37.5038, lng: 126.9380 }, { lat: 37.5035, lng: 126.9410 }, { lat: 37.5025, lng: 126.9425 }, { lat: 37.4998, lng: 126.9415 }, { lat: 37.5005, lng: 126.9385 }, { lat: 37.5015, lng: 126.9372 }],
        center: { lat: 37.5015, lng: 126.9400 }
    },
    sangdo15: {
        category: 'shintong', group: '신속통합/재개발', label: '상도 15구역', status: '정비구역지정', color: '#FF9800',
        path: [{ lat: 37.5005, lng: 126.9350 }, { lat: 37.5005, lng: 126.9385 }, { lat: 37.4985, lng: 126.9415 }, { lat: 37.4965, lng: 126.9410 }, { lat: 37.4948, lng: 126.9385 }, { lat: 37.4955, lng: 126.9355 }, { lat: 37.4980, lng: 126.9335 }],
        center: { lat: 37.4975, lng: 126.9375 }
    },
    sadang15: { category: 'shintong', group: '신속통합/재개발', label: '사당 15구역', center: { lat: 37.4795, lng: 126.9742 }, color: '#FF5722', status: '구역지정 추진중', path: [{ lat: 37.4815, lng: 126.9725 }, { lat: 37.4815, lng: 126.9765 }, { lat: 37.4775, lng: 126.9765 }, { lat: 37.4775, lng: 126.9725 }] },
    sadang17: { category: 'shintong', group: '신속통합/재개발', label: '사당 17구역', center: { lat: 37.4921, lng: 126.9755 }, color: '#FF9800', status: '정비구역지정', path: [{ lat: 37.494, lng: 126.974 }, { lat: 37.494, lng: 126.977 }, { lat: 37.491, lng: 126.977 }, { lat: 37.491, lng: 126.974 }] },

    // 모아타운
    moa_sadang: { category: 'moa', group: '모아타운', label: '사당 202-29 일대', center: { lat: 37.4831, lng: 126.9815 }, color: '#4CAF50', status: '관리계획수립완료', path: [{ lat: 37.484, lng: 126.980 }, { lat: 37.484, lng: 126.983 }, { lat: 37.482, lng: 126.983 }, { lat: 37.482, lng: 126.980 }] },
    moa_dongjak: { category: 'moa', group: '모아타운', label: '동작 102-8 일대', center: { lat: 37.4985, lng: 126.9821 }, color: '#4CAF50', status: '대상지선정', path: [{ lat: 37.500, lng: 126.981 }, { lat: 37.500, lng: 126.984 }, { lat: 37.497, lng: 126.984 }, { lat: 37.497, lng: 126.981 }] },
    moa_noryangjin: { category: 'moa', group: '모아타운', label: '노량진 221-24 일대', center: { lat: 37.5055, lng: 126.9415 }, color: '#4CAF50', status: '조합설립중', path: [{ lat: 37.507, lng: 126.940 }, { lat: 37.507, lng: 126.943 }, { lat: 37.504, lng: 126.943 }, { lat: 37.504, lng: 126.940 }] },
    moa_sangdo242: { category: 'moa', group: '모아타운', label: '상도 242 일대', center: { lat: 37.5021, lng: 126.9485 }, color: '#4CAF50', status: '대상지선정', path: [{ lat: 37.504, lng: 126.947 }, { lat: 37.504, lng: 126.950 }, { lat: 37.501, lng: 126.950 }, { lat: 37.501, lng: 126.947 }] }
};

const Land = () => {
    const [loading, setLoading] = useState(false);
    const [estateList, setEstateList] = useState([]);
    const [selectedEstate, setSelectedEstate] = useState(null);
    const [map, setMap] = useState(null);
    const [roadview, setRoadview] = useState(null);
    const [showRoadview, setShowRoadview] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [transactions, setTransactions] = useState([]);
    const [isTransLoading, setIsTransLoading] = useState(false);
    const [showAllTransactions, setShowAllTransactions] = useState(false);
    const [activeGroup, setActiveGroup] = useState(null); // 동일 좌표 매물 그룹 모달 상태

    // 정비사업 구역 표시 상태
    const [activeDevelopments, setActiveDevelopments] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showDevLayer, setShowDevLayer] = useState(false);
    const [devSearchQuery, setDevSearchQuery] = useState('');

    // 검색 필터 상태
    const [filters, setFilters] = useState({
        cortar_no: '', // 기본 전체
        confirm_date: format(new Date(), 'yyyy-MM-dd'),
        trade_type: 'A1,B1,B2', // 기본 매매, 전세, 월세
        article_status: 'R0',
        real_estate_type: 'A01,A02,C01,C02,A06,A05,C03,D01,D03,D02,D04,D05,E04',
        supply_area: '',
        exclusive_area: '',
        min_price: '',
        max_price: '',
        min_area: '',
        max_area: '',
        article_name: ''
    });

    const [tempFilters, setTempFilters] = useState({ ...filters });
    const [areaUnit, setAreaUnit] = useState('m2'); // 'm2' or 'py'

    // 건축물 정보 관련 상태
    const [activeDetailTab, setActiveDetailTab] = useState('detail');
    const [krasData, setKrasData] = useState({
        title: null, recap: null, basis: null, floor: null,
        exposArea: null, hsprc: null, expos: null,
        wclf: null, atchJibun: null, jijigu: null
    });
    const [isKrasLoading, setIsKrasLoading] = useState({
        title: false, recap: false, basis: false, floor: false,
        exposArea: false, hsprc: false, expos: false,
        wclf: false, atchJibun: false, jijigu: false
    });
    // 정비사업 그룹화 데이터
    const groupedDevelopments = useMemo(() => {
        const groups = {};
        Object.entries(DONGJAK_DEVELOPMENTS).forEach(([key, area]) => {
            if (!groups[area.group]) groups[area.group] = [];
            groups[area.group].push({ key, ...area });
        });
        return groups;
    }, []);

    // 금리 정보 관련 상태
    const [interestRates, setInterestRates] = useState([]);
    const [rateIndex, setRateIndex] = useState(0);

    // 세금/비용 계산기 관련 상태
    const [isFirstHome, setIsFirstHome] = useState(false);
    const [taxData, setTaxData] = useState({
        price: 0,
        acqTax: 0,
        eduTax: 0,
        ruralTax: 0,
        stampDuty: 0,
        bondFee: 0,
        brokerFee: 0,
        legalFee: 600000,
        movingFee: 1500000
    });

    // 탭 스크롤 관련 (PC 드래그)
    const tabScrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - tabScrollRef.current.offsetLeft);
        setScrollLeft(tabScrollRef.current.scrollLeft);
    };
    const handleMouseLeave = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - tabScrollRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        tabScrollRef.current.scrollLeft = scrollLeft - walk;
    };
    const scrollTabs = (dir) => {
        if (tabScrollRef.current) {
            tabScrollRef.current.scrollBy({ left: dir === 'left' ? -200 : 200, behavior: 'smooth' });
        }
    };
    const krasTabs = useMemo(() => {
        const tabs = [{ key: 'detail', label: '매물상세' }];

        // 매매(trade_type === 'A1')일 때만 세금/비용 탭 추가
        if (selectedEstate?.trade_type === 'A1') {
            tabs.push({ key: 'tax', label: '세금/비용' });
        }

        tabs.push(
            { key: 'title', label: '표제부', service: 'getBrTitleInfo' },
            { key: 'recap', label: '총괄표제부', service: 'getBrRecapTitleInfo' },
            { key: 'basis', label: '기본개요', service: 'getBrBasisOulnInfo' },
            { key: 'floor', label: '층별현황', service: 'getBrFlrOulnInfo' },
            { key: 'exposArea', label: '전유공용면적', service: 'getBrExposPubuseAreaInfo' },
            { key: 'hsprc', label: '주택가격', service: 'getBrHsprcInfo' },
            { key: 'expos', label: '전유부', service: 'getBrExposInfo' },
            { key: 'wclf', label: '오수정화시설', service: 'getBrWclfInfo' },
            { key: 'atchJibun', label: '부속지번', service: 'getBrAtchJibunInfo' },
            { key: 'jijigu', label: '지역지구구역', service: 'getBrJijiguInfo' }
        );
        return tabs;
    }, [selectedEstate?.trade_type]);

    // 정비사업 구역 토글 헬퍼
    const toggleDevelopment = (key) => {
        setActiveDevelopments(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    // 세금 계산 헬퍼 함수
    const getCalculatedTaxData = useCallback((price, area, isFirst) => {
        // 1. 취득세
        let acqRate = 0.01;
        if (price > 900000000) acqRate = 0.03;
        else if (price > 600000000) acqRate = parseFloat(((price * 2 / 300000000 - 3) / 100).toFixed(4));
        let acqTax = Math.floor(price * acqRate);
        if (isFirst) acqTax = Math.max(0, acqTax - 2000000);

        // 2. 지방교육세
        const eduTax = Math.floor(acqTax * 0.1);

        // 3. 농어촌특별세 (85㎡ 초과 시 0.2%)
        const ruralTax = area > 85 ? Math.floor(price * 0.002) : 0;

        // 4. 인지세
        let stampDuty = 0;
        if (price > 1000000000) stampDuty = 350000;
        else if (price > 100000000) stampDuty = 150000;
        else if (price > 50000000) stampDuty = 70000;
        else if (price > 30000000) stampDuty = 40000;
        else if (price > 10000000) stampDuty = 20000;

        // 5. 국민주택채권 (약 0.25% 가정)
        const bondFee = Math.floor(price * 0.0025);

        // 6. 중개수수료 (주택 매매 상한요율)
        let brokerRate = 0.004;
        let brokerLimit = Infinity;
        if (price < 50000000) { brokerRate = 0.006; brokerLimit = 250000; }
        else if (price < 200000000) { brokerRate = 0.005; brokerLimit = 800000; }
        else if (price < 900000000) brokerRate = 0.004;
        else if (price < 1200000000) brokerRate = 0.005;
        else if (price < 1500000000) brokerRate = 0.006;
        else brokerRate = 0.007;

        let brokerFee = Math.floor(price * brokerRate);
        if (brokerFee > brokerLimit) brokerFee = brokerLimit;
        brokerFee = Math.floor(brokerFee * 1.1); // 부가세 10% 포함

        return {
            price,
            acqTax,
            eduTax,
            ruralTax,
            stampDuty,
            bondFee,
            brokerFee,
            legalFee: 600000,
            movingFee: 1500000
        };
    }, []);

    // 세금 계산 로직
    const calculateTax = useCallback(() => {
        if (!selectedEstate) return;

        // 가격 파싱 (만원 단위 -> 원)
        const rawPrice = selectedEstate.price || selectedEstate.deal_price || selectedEstate.dealPrice || 0;
        const price = rawPrice * 10000;
        const area = parseFloat(selectedEstate.exclusive_area || selectedEstate.area || 0);

        const newTaxData = getCalculatedTaxData(price, area, isFirstHome);
        setTaxData(newTaxData);
    }, [selectedEstate, isFirstHome, getCalculatedTaxData]);

    // 매물 선택 시 세금 계산 초기화
    useEffect(() => {
        if (selectedEstate?.trade_type === 'A1') {
            calculateTax();
        }
    }, [selectedEstate, calculateTax]);

    const handleTaxInputChange = (name, value) => {
        const numValue = parseInt(value.replace(/[^0-9]/g, '') || 0);

        if (name === 'price') {
            const area = parseFloat(selectedEstate?.exclusive_area || selectedEstate?.area || 0);
            const newTaxData = getCalculatedTaxData(numValue, area, isFirstHome);
            setTaxData(newTaxData);
        } else {
            setTaxData(prev => ({
                ...prev,
                [name]: numValue
            }));
        }
    };

    const renderDevelopmentLayer = () => {
        const filteredGroups = {};
        Object.entries(groupedDevelopments).forEach(([groupName, areas]) => {
            const matches = areas.filter(a => a.label.includes(devSearchQuery) || a.status.includes(devSearchQuery));
            if (matches.length > 0) filteredGroups[groupName] = matches;
        });

        const layerContent = (
            <>
                <div className="p-3 border-bottom bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-bold">동작구 정비사업</h6>
                        <Button variant="link" size="sm" className="text-dark p-0" onClick={() => setShowDevLayer(false)}>
                            {isMobile ? <FaChevronDown /> : <FaChevronRight />}
                        </Button>
                    </div>
                    <InputGroup size="sm">
                        <InputGroup.Text className="bg-light border-end-0"><FaSearch size={12} className="text-muted" /></InputGroup.Text>
                        <Form.Control
                            placeholder="구역명, 추진단계 검색..."
                            value={devSearchQuery}
                            onChange={(e) => setDevSearchQuery(e.target.value)}
                            className="bg-light border-start-0"
                        />
                    </InputGroup>
                </div>
                <div className="flex-grow-1 overflow-auto bg-white">
                    {Object.entries(filteredGroups).map(([groupName, areas]) => (
                        <div key={groupName}>
                            <div className="px-3 py-2 bg-light small fw-bold text-muted border-bottom border-top" style={{ fontSize: '11px' }}>{groupName}</div>
                            {areas.map(area => (
                                <div
                                    key={area.key}
                                    className="dev-item-card"
                                    onClick={() => toggleDevelopment(area.key)}
                                >
                                    <div>
                                        <span className="small fw-bold" style={{ fontSize: '13px' }}>{area.label}</span>
                                        <br />
                                        <span className="status-badge" style={{ margin: 0 }}>{area.status}</span>
                                    </div>
                                    <Form.Check
                                        type="checkbox"
                                        checked={activeDevelopments.includes(area.key)}
                                        readOnly
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                    {Object.keys(filteredGroups).length === 0 && (
                        <div className="p-5 text-center text-muted small">조회된 정비사업 구역이 없습니다.</div>
                    )}
                </div>
                <div className="p-3 border-top bg-light d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" className="flex-grow-1 fw-bold" onClick={() => setActiveDevelopments([])}>초기화</Button>
                    <Button variant="primary" size="sm" className="flex-grow-1 fw-bold" onClick={() => setShowDevLayer(false)}>닫기</Button>
                </div>
            </>
        );

        if (isMobile) {
            return (
                <div className="dev-bottom-sheet" style={{ transform: showDevLayer ? 'translateY(0)' : 'translateY(100%)' }}>
                    <div className="dev-sheet-handle" onClick={() => setShowDevLayer(false)}></div>
                    {layerContent}
                </div>
            );
        }

        return (
            <div className="dev-sidebar" style={{ transform: showDevLayer ? 'translateX(0)' : 'translateX(100%)' }}>
                {layerContent}
            </div>
        );
    };

    // 면적 변환 (㎡ -> 평)
    const m2toPy = (m2) => {
        const val = parseFloat(m2);
        if (!val || isNaN(val) || val <= 0) return '';
        return ` ${(val * 0.3025).toFixed(0)}평`;
    };

    // 등록일자 옵션 리스트
    const dateOptions = useMemo(() => {
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        const options = ['']; // '전체' 옵션 추가
        let curr = new Date();
        while (curr >= start) {
            options.push(format(curr, 'yyyy-MM-dd'));
            curr.setDate(curr.getDate() - 1);
        }
        return options;
    }, []);

    const handleFilterChange = (e) => {
        const { name, value, tagName } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setTempFilters(prev => ({ ...prev, [name]: value }));

        if (tagName === 'SELECT') {
            e.target.blur();
        }
    };

    const toggleMultiSelect = (name, value) => {
        setTempFilters(prev => {
            const current = prev[name] ? prev[name].split(',') : [];
            let next;
            if (current.includes(value)) {
                // 최소 하나는 선택되어야 함
                if (current.length <= 1) return prev;
                next = current.filter(item => item !== value);
            } else {
                next = [...current, value];
            }
            return { ...prev, [name]: next.join(',') };
        });
    };

    const handleRangeSelect = (type, value) => {
        const minKey = type === 'price' ? 'min_price' : 'min_area';
        const maxKey = type === 'price' ? 'max_price' : 'max_area';

        setTempFilters(prev => {
            const min = prev[minKey];
            const max = prev[maxKey];

            if (!min || (min && max)) {
                // 새로 시작
                return { ...prev, [minKey]: value, [maxKey]: '' };
            } else {
                // 범위 설정
                const numMin = parseFloat(min);
                const numVal = parseFloat(value);
                if (numVal < numMin) {
                    return { ...prev, [minKey]: value.toString(), [maxKey]: min };
                } else {
                    return { ...prev, [maxKey]: value.toString() };
                }
            }
        });
    };

    const handleTempFilterChange = (name, value) => {
        setTempFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setFilters({ ...tempFilters });
        document.body.click(); // 팝오버 닫기용 편법
    };

    const resetFilters = (type) => {
        if (type === 'price') {
            setTempFilters(prev => ({ ...prev, min_price: '', max_price: '' }));
        } else if (type === 'area') {
            setTempFilters(prev => ({ ...prev, supply_area: '', exclusive_area: '' }));
        } else {
            setTempFilters({ ...filters });
        }
    };

    // 로드뷰 표시 토글 및 위치 갱신
    const toggleRoadview = useCallback((estate) => {
        if (!estate || !roadview) return;

        // 1. 먼저 컨테이너를 표시하여 크기가 잡히도록 함
        setShowRoadview(true);

        // 2. 렌더링 후 처리를 위해 약간의 지연 후 실행
        setTimeout(() => {
            const rvClient = new window.kakao.maps.RoadviewClient();
            const position = new window.kakao.maps.LatLng(estate.latitude, estate.longitude);

            // 레이아웃 재배치 (컨테이너 크기 변경 대응)
            roadview.relayout();

            rvClient.getNearestPanoId(position, 100, (panoId) => {
                if (panoId) {
                    // Pano ID 설정 (두 번째 인자 제거하여 기본 동작 보장)
                    roadview.setPanoId(panoId);

                    // 3. 시점(POV)조정 - 파노라마가 로드된 후 실행되도록 다시 지연
                    setTimeout(() => {
                        const rvPos = roadview.getPosition();
                        const propPos = position;

                        const dy = propPos.getLat() - rvPos.getLat();
                        const dx = propPos.getLng() - rvPos.getLng();
                        const angle = Math.atan2(dx, dy) * (180 / Math.PI);

                        roadview.setViewpoint({
                            pan: angle,
                            tilt: 0, // 바닥을 보지 않도록 수평 고정
                            zoom: 0
                        });
                    }, 500);
                } else {
                    alert("해당 위치의 로드뷰 정보가 없습니다.");
                    setShowRoadview(false);
                }
            });
        }, 100);
    }, [roadview]);

    // 실거래가 조회
    const fetchTransactions = useCallback(async (params) => {
        setIsTransLoading(true);
        setTransactions([]);
        try {
            const response = await axiosInstance.post('estate/transactions', params);
            if (response.data.status === "0000") {
                setTransactions(response.data.data.transactionsList || []);
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
        } finally {
            setIsTransLoading(false);
        }
    }, []);

    // 건축물 정보(Kras) 조회
    const fetchKrasData = useCallback(async (serviceType) => {
        if (!selectedEstate) return;

        const typeMap = {
            'getBrTitleInfo': 'title',
            'getBrRecapTitleInfo': 'recap',
            'getBrBasisOulnInfo': 'basis',
            'getBrFlrOulnInfo': 'floor',
            'getBrExposPubuseAreaInfo': 'exposArea',
            'getBrHsprcInfo': 'hsprc',
            'getBrExposInfo': 'expos',
            'getBrWclfInfo': 'wclf',
            'getBrAtchJibunInfo': 'atchJibun',
            'getBrJijiguInfo': 'jijigu'
        };
        const type = typeMap[serviceType];
        if (!type || krasData[type]) return; // 이미 데이터가 있으면 요청 안함 (Cashing)

        setIsKrasLoading(prev => ({ ...prev, [type]: true }));

        try {
            // bcode 추출 (selectedEstate.bcode가 없으면 fallback으로 cortar_no 사용)
            const bcode = selectedEstate.bcode || selectedEstate.cortar_no || '';
            const sigunguCd = bcode.substring(0, 5);
            const bjdongCd = bcode.substring(5, 10);

            // 지번 (stored in selectedEstate during handleEstateSelect)
            const bun = (selectedEstate.bun || '0000').padStart(4, '0');
            const ji = (selectedEstate.ji || '0000').padStart(4, '0');

            if (!sigunguCd || !bjdongCd) {
                console.warn("Missing required codes for KRAS API:", { sigunguCd, bjdongCd });
                setKrasData(prev => ({ ...prev, [type]: [] }));
                return;
            }

            const params = {
                sigunguCd,
                bjdongCd,
                platGbCd: '0',
                bun,
                ji,
                serviceType
            };

            const response = await axiosInstance.post('estate/kras', params);
            if (response.data.status === "0000") {
                const items = response.data.data.krasData?.response?.body?.items?.item;
                const krasItems = items ? (Array.isArray(items) ? items : [items]) : [];
                setKrasData(prev => ({ ...prev, [type]: krasItems }));
            } else {
                setKrasData(prev => ({ ...prev, [type]: [] }));
            }
        } catch (error) {
            console.error(`KRAS API Error (${serviceType}):`, error);
            setKrasData(prev => ({ ...prev, [type]: [] }));
        } finally {
            setIsKrasLoading(prev => ({ ...prev, [type]: false }));
        }
    }, [selectedEstate, krasData]);

    // 공통 건축물 정보 렌더러
    const renderKrasCommonData = (type, label) => {
        const data = krasData[type];
        const isLoading = isKrasLoading[type];

        if (isLoading) {
            return (
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted small">데이터를 불러오는 중입니다...</p>
                </div>
            );
        }

        if (!data || data.length === 0) {
            return (
                <div className="alert alert-light text-center p-5 border small">
                    <p className="mb-0 text-muted">선택하신 정보에 대한 데이터가 없습니다.</p>
                </div>
            );
        }

        return (
            <div className={`kras - ${type} -section p - 3`}>
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                    <FaBuilding className="text-primary me-2" /> {label}
                </h6>
                {data.map((item, idx) => {
                    // 데이터 전처리: 번/지 통합 및 0 제거 포맷팅
                    const processedItem = { ...item };
                    const formatJibun = (main, sub) => {
                        const m = parseInt(main, 10);
                        const s = parseInt(sub, 10);
                        if (isNaN(m)) return '-';
                        return isNaN(s) || s === 0 ? `${m}` : `${m}-${s}`;
                    };

                    if (item.bun !== undefined && item.ji !== undefined) {
                        processedItem.jibun = formatJibun(item.bun, item.ji);
                    }
                    if (item.naMainBun !== undefined && item.naSubBun !== undefined) {
                        processedItem.naJibun = formatJibun(item.naMainBun, item.naSubBun);
                    }

                    // 대표여부 변환 (0: 일반, 1: 대표)
                    if (item.reprYn !== undefined) {
                        processedItem.reprYn = item.reprYn === '1' ? '대표' : '일반';
                    }

                    // 금액 및 날짜 포맷팅
                    if (item.hsprc !== undefined) {
                        processedItem.hsprc = item.hsprc ? Number(item.hsprc).toLocaleString() : '0';
                    }
                    if (item.crtnDay !== undefined) {
                        const d = String(item.crtnDay);
                        if (d.length === 8) processedItem.crtnDay = `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(6, 8)}`;
                    }
                    if (item.stdDay !== undefined) {
                        const d = String(item.stdDay);
                        if (d.length === 8) processedItem.stdDay = `${d.substring(0, 4)}-${d.substring(4, 6)}-${d.substring(6, 8)}`;
                    }

                    // krasLabelMap에 정의된 키들만 필터링
                    const validEntries = Object.entries(processedItem).filter(([key]) => krasLabelMap[key]);

                    if (validEntries.length === 0) return null;

                    return (
                        <div key={idx} className="mb-4">
                            <h6 className="fw-bold mb-2 border-start border-primary border-4 ps-2 small text-primary">#{idx + 1} 상세내용</h6>
                            <Table bordered hover size="sm" className="mb-0 small align-middle">
                                <tbody>
                                    {validEntries.reduce((acc, [key, val], i, arr) => {
                                        if (i % 2 === 0) {
                                            const next = arr[i + 1];
                                            const label1 = krasLabelMap[key];
                                            const label2 = next ? krasLabelMap[next[0]] : '';
                                            acc.push(
                                                <tr key={i}>
                                                    <th className="table-light text-center" style={{ width: '20%', backgroundColor: '#f8f9fa' }}>{label1}</th>
                                                    <td style={{ width: '30%' }}>{val || '-'}</td>
                                                    {next ? (
                                                        <>
                                                            <th className="table-light text-center" style={{ width: '20%', backgroundColor: '#f8f9fa' }}>{label2}</th>
                                                            <td style={{ width: '30%' }}>{next[1] || '-'}</td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th className="table-light text-center" style={{ width: '20%', backgroundColor: '#f8f9fa' }}></th>
                                                            <td style={{ width: '30%' }}></td>
                                                        </>
                                                    )}
                                                </tr>
                                            );
                                        }
                                        return acc;
                                    }, [])}
                                </tbody>
                            </Table>
                        </div>
                    );
                })}
            </div>
        );
    };

    const handleEstateSelect = useCallback((estate, shouldPan = true) => {
        // 매물 선택 시 탭 스크롤 초기화
        if (tabScrollRef.current) {
            tabScrollRef.current.scrollLeft = 0;
        }

        if (!estate) {
            setSelectedEstate(null);
            setShowRoadview(false);
            setTransactions([]);
            setShowAllTransactions(false);
            setActiveDetailTab('detail');
            setKrasData({
                title: null, recap: null, basis: null, floor: null,
                exposArea: null, hsprc: null, expos: null,
                wclf: null, atchJibun: null, jijigu: null
            });
            return;
        }

        setShowAllTransactions(false);
        setActiveDetailTab('detail');
        setKrasData({
            title: null, recap: null, basis: null, floor: null,
            exposArea: null, hsprc: null, expos: null,
            wclf: null, atchJibun: null, jijigu: null
        });

        // 우선 매물 정보만이라도 먼저 표시 (API 로딩 전)
        setSelectedEstate({ ...estate });

        // 주소 정보 추출 (Geocoder)
        const geocoder = new window.kakao.maps.services.Geocoder();
        geocoder.coord2Address(estate.longitude, estate.latitude, (result, status) => {
            if (status === window.kakao.maps.services.Status.OK && result[0]) {
                const addr = result[0].address;
                const road = result[0].road_address;
                const address = road ? road.address_name : (addr ? addr.address_name : '-');

                // KRAS 파라미터 (지번)
                const bun = addr?.main_address_no || '';
                const ji = addr?.sub_address_no || '';

                // 실거래가 파라미터 구성
                if (addr) {
                    const houseType = (estate.real_estate_type === 'C02' || estate.real_estate_type === 'C03') ? 'VLA' :
                        (estate.real_estate_type === 'A06' || estate.real_estate_type === 'A05') ? 'HSE' : 'APT';

                    const transParams = {
                        region_cd: addr.b_code ? addr.b_code.substring(0, 5) : '',
                        umd_nm: addr.region_3depth_name || '',
                        jibun: addr.main_address_no ? (addr.main_address_no + (addr.sub_address_no ? '-' + addr.sub_address_no : '')) : '',
                        house_type: houseType
                    };

                    setSelectedEstate(prev => ({
                        ...prev,
                        address,
                        bcode: addr.b_code,
                        bun: bun,
                        ji: ji
                    }));
                    fetchTransactions(transParams);
                } else {
                    setSelectedEstate(prev => ({ ...prev, address, bun, ji }));
                }
            }
        });

        if (shouldPan && map) {
            const position = new window.kakao.maps.LatLng(estate.latitude, estate.longitude);
            map.setCenter(position);
            map.setLevel(3);
            setTimeout(() => map.setCenter(position), 100);
        }

        setShowRoadview(false);
        // 매물 선택 시 화면 맨 위로 스크롤 이동
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [map, fetchTransactions]);

    const fetchEstateData = useCallback(async () => {
        setLoading(true);
        setSelectedEstate(null);
        setShowRoadview(false);
        try {
            const response = await axiosInstance.post('estate/articles', filters);
            if (response.data.status === "0000") {
                const data = response.data.data.estateList
                    .map(item => item.json)
                    .filter(item => item && item.article_no);
                setEstateList(data);
            } else {
                setEstateList([]);
                alert(response.data.message || "조회 중 오류가 발생했습니다.");
            }
        } catch (error) {
            console.error("Error fetching estate data:", error);
            setEstateList([]);
            alert("서버 통신 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // 정렬 처리
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // 정렬된 리스트
    const sortedEstateList = useMemo(() => {
        let items = [...estateList];
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // 가격 정렬 특수 처리: price (숫자) 필드가 있으면 사용
                if (sortConfig.key === 'price_text') {
                    aVal = a.price || aVal;
                    bVal = b.price || bVal;
                }

                // 숫자 변환 시도 (면적, 가격 등)
                const numA = parseFloat(aVal);
                const numB = parseFloat(bVal);
                if (!isNaN(numA) && !isNaN(numB)) {
                    aVal = numA;
                    bVal = numB;
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [estateList, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <span style={{ opacity: 0.3, marginLeft: '5px' }}><FaChevronDown size={10} /></span>;
        return sortConfig.direction === 'asc' ? <FaChevronUp size={10} style={{ marginLeft: '5px' }} /> : <FaChevronDown size={10} style={{ marginLeft: '5px' }} />;
    };

    // 카카오맵 초기화
    useEffect(() => {
        const script = document.createElement("script");
        script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=252789b38c66bb62dc7212f1c1176ea0&libraries=services,clusterer,drawing&autoload=false`;
        script.async = true;
        document.head.appendChild(script);

        script.onload = () => {
            window.kakao.maps.load(() => {
                const container = document.getElementById('map');
                if (!container) return;

                const options = {
                    center: new window.kakao.maps.LatLng(37.494288, 126.931407),
                    level: 5
                };
                const kakaoMap = new window.kakao.maps.Map(container, options);

                const mapTypeControl = new window.kakao.maps.MapTypeControl();
                kakaoMap.addControl(mapTypeControl, window.kakao.maps.ControlPosition.TOPRIGHT);

                const zoomControl = new window.kakao.maps.ZoomControl();
                kakaoMap.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

                setMap(kakaoMap);

                // 로드뷰 초기화 (컨테이너는 항상 존재)
                const rvContainer = document.getElementById('roadview');
                if (rvContainer) {
                    const rv = new window.kakao.maps.Roadview(rvContainer);
                    setRoadview(rv);
                }
            });
        };

        return () => {
            const existingScript = document.querySelector('script[src*="kakao.com"]');
            if (existingScript && existingScript.parentNode) {
                existingScript.parentNode.removeChild(existingScript);
            }
        };
    }, []);

    // 마커 및 오버레이 업데이트
    useEffect(() => {
        if (!map) return;
        const activeMarkers = [];
        const activeOverlays = [];

        if (estateList.length > 0) {
            const bounds = new window.kakao.maps.LatLngBounds();

            // 1. 좌표 기반 그룹화 전처리
            const groupedEstates = estateList.reduce((acc, item) => {
                if (!item.latitude || !item.longitude) return acc;
                const key = `${item.latitude},${item.longitude}`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
            }, {});

            // 2. 그룹별 렌더링
            Object.values(groupedEstates).forEach(group => {
                const item = group[0]; // 대표 매물
                const position = new window.kakao.maps.LatLng(item.latitude, item.longitude);
                const isGroup = group.length > 1;

                const marker = new window.kakao.maps.Marker({
                    position: position,
                    map: map,
                    zIndex: isGroup ? 5 : 3
                });

                const contentDiv = document.createElement('div');
                const tradeColor = tradeTypeColorMap[item.trade_type] || '#007bff';

                // 그룹 여부에 따른 스타일 및 텍스트 설정
                const borderStyle = isGroup ? `3px solid ${tradeColor}` : `2px solid ${tradeColor}`;
                const extraShadow = isGroup ? '0 4px 10px rgba(0,0,0,0.3)' : '2px 2px 5px rgba(0,0,0,0.2)';

                contentDiv.style.cssText = `padding: 5px 10px; background: white; border: ${borderStyle}; color: ${tradeColor}; border-radius: 5px; font-size: 11px; font-weight: bold; white-space: nowrap; cursor: pointer; transform: translateY(-45px); box-shadow: ${extraShadow};`;

                const estateType = estateTypeMap[item.real_estate_type] || item.real_estate_type;
                const priceDisplay = item.trade_type === 'B2' && item.rent_price
                    ? `${item.price_text} / ${item.rent_price.toLocaleString()}`
                    : item.price_text;
                const mainText = `${estateType} / ${tradeTypeMap[item.trade_type] || item.trade_type} / ${priceDisplay} / ${m2toPy(item.exclusive_area).trim()}`;
                const groupSuffix = isGroup ? ` <span style="color:#d9534f; margin-left:5px;">외 ${group.length - 1}건</span>` : '';

                contentDiv.innerHTML = `${mainText}${groupSuffix}`;

                contentDiv.onclick = (e) => {
                    e.stopPropagation();
                    if (isGroup) {
                        setActiveGroup(group);
                    } else {
                        handleEstateSelect(item, false);
                    }
                };

                const overlay = new window.kakao.maps.CustomOverlay({
                    position: position,
                    content: contentDiv,
                    yAnchor: 1,
                    zIndex: isGroup ? 6 : 4
                });
                overlay.setMap(map);

                window.kakao.maps.event.addListener(marker, 'click', () => {
                    if (isGroup) {
                        setActiveGroup(group);
                    } else {
                        handleEstateSelect(item, false);
                    }
                });

                activeMarkers.push(marker);
                activeOverlays.push(overlay);
                bounds.extend(position);
            });

            if (activeMarkers.length > 0) {
                map.setBounds(bounds);
            }
        }

        return () => {
            activeMarkers.forEach(m => m.setMap(null));
            activeOverlays.forEach(o => o.setMap(null));
        };
    }, [map, estateList, handleEstateSelect]);

    // 동작구 정비사업 구역 표시 useEffect
    useEffect(() => {
        if (!map) return;

        const polygons = [];
        const overlays = [];

        activeDevelopments.forEach(areaKey => {
            const area = DONGJAK_DEVELOPMENTS[areaKey];
            if (!area) return;

            const color = area.color;
            const path = area.path.map(coord => new window.kakao.maps.LatLng(coord.lat, coord.lng));

            // 폴리곤 생성
            const polygon = new window.kakao.maps.Polygon({
                path: path,
                strokeWeight: 3,
                strokeColor: color,
                strokeOpacity: 0.8,
                strokeStyle: 'solid',
                fillColor: color,
                fillOpacity: 0.2
            });
            polygon.setMap(map);
            polygons.push(polygon);

            // 구역 명칭 오버레이 생성
            const contentDiv = document.createElement('div');
            contentDiv.style.cssText = `padding: 3px 8px; background: ${color}; color: white; border-radius: 4px; font-size: 11px; font-weight: bold; box-shadow: 1px 1px 3px rgba(0,0,0,0.3); pointer-events: none; white-space: nowrap;`;
            contentDiv.innerText = area.label.split(' ').pop(); // '노량진 1구역' -> '1구역' 형태로 축약 표시

            const overlay = new window.kakao.maps.CustomOverlay({
                position: new window.kakao.maps.LatLng(area.center.lat, area.center.lng),
                content: contentDiv,
                yAnchor: 0.5,
                zIndex: 5
            });
            overlay.setMap(map);
            overlays.push(overlay);
        });

        return () => {
            polygons.forEach(p => p.setMap(null));
            overlays.forEach(o => o.setMap(null));
        };
    }, [map, activeDevelopments]);

    // 반응형 대응 리스너
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 지역 필터가 동작구가 아니면 정비사업 표시 상태 초기화 (동작구 전역 보기 '11590' 대응)
    useEffect(() => {
        if (filters.cortar_no && !filters.cortar_no.startsWith('11590')) {
            setActiveDevelopments([]);
        }
    }, [filters.cortar_no]);

    useEffect(() => {
        fetchEstateData();
    }, [fetchEstateData]);

    // 금리 정보 가져오기
    useEffect(() => {
        const fetchRates = async () => {
            try {
                const response = await axiosInstance.post('estate/interest-rates');
                if (response.data.status === "0000" && response.data.data.krasData.searchLoOrgIrList.row) {
                    setInterestRates(response.data.data.krasData.searchLoOrgIrList.row);
                }
            } catch (error) {
                console.error("Error fetching interest rates:", error);
            }
        };
        fetchRates();
    }, []);

    // 금리 정보 롤링 타이머
    useEffect(() => {
        if (interestRates.length === 0) return;
        const timer = setInterval(() => {
            setRateIndex(prev => (prev + 1) % interestRates.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [interestRates]);

    // 은행 로고 URL 매핑 (로컬 파일 사용)
    const getBankLogoUrl = (orgCd) => {
        const mapping = {
            'B01': '/images/banks/kb.png',
            'B02': '/images/banks/nonghyup.png',
            'B05': '/images/banks/saemaeul.png',
            'B06': '/images/banks/shinhan.png',
            'B07': '/images/banks/woori.png',
        };
        return mapping[orgCd] || '';
    };

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0"><FaBuilding /> 와방</h2>
            </div>

            {/* 금리 정보 롤링 바 */}
            {interestRates.length > 0 && (
                <div className="interest-rate-rolling-bar shadow-sm">
                    <div className="container-fluid d-flex align-items-center py-2 bg-white border-bottom overflow-hidden" style={{ height: '40px' }}>
                        <div className="rate-label d-flex align-items-center me-3 border-end pe-3 text-primary fw-bold" style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                            <span className="me-2">금리 정보</span>
                            <div className="spinner-grow spinner-grow-sm text-primary" role="status" style={{ width: '8px', height: '8px' }}></div>
                        </div>
                        <div className="rate-content-wrapper flex-grow-1 position-relative" style={{ height: '24px' }}>
                            {interestRates.map((rate, idx) => (
                                <div
                                    key={idx}
                                    className={`rate-item position-absolute w-100 d-flex align-items-center gap-3 ${idx === rateIndex ? 'active' : 'inactive'}`}
                                    style={{ transition: 'all 0.5s ease-in-out', visibility: idx === rateIndex ? 'visible' : 'hidden', opacity: idx === rateIndex ? 1 : 0, transform: idx === rateIndex ? 'translateY(0)' : 'translateY(20px)' }}
                                >
                                    <div className="bank-info d-flex align-items-center" style={{ minWidth: 'fit-content' }}>
                                        {getBankLogoUrl(rate.lo_org_cd) ? (
                                            <img
                                                src={getBankLogoUrl(rate.lo_org_cd)}
                                                alt={rate.lo_org_nm}
                                                className="me-2 rounded-circle"
                                                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                            />
                                        ) : null}
                                        <div className="bank-logo-circle me-2 bg-light border rounded-circle fw-bold text-secondary" style={{ width: '24px', height: '24px', fontSize: '10px', display: getBankLogoUrl(rate.lo_org_cd) ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {rate.lo_org_nm.substring(0, 1)}
                                        </div>
                                        <span className="bank-name fw-bold" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{rate.lo_org_nm}</span>
                                    </div>
                                    <div className="rate-info d-flex align-items-center">
                                        <span className="rate-text text-danger fw-bold" style={{ fontSize: '15px', whiteSpace: 'nowrap' }}>
                                            {rate.min_lo_ir}% ~ {rate.max_lo_ir}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* 네이버 부동산 스타일 필터 바 */}
            <div className="filter-bar d-flex align-items-center flex-wrap gap-2 p-2 border-bottom bg-white">
                {/* 법정동 선택 */}
                <OverlayTrigger
                    trigger="click"
                    placement="bottom-start"
                    rootClose
                    overlay={
                        <Popover id="popover-cortar" style={{ minWidth: '220px' }}>
                            <Popover.Header as="h3">지역 선택</Popover.Header>
                            <Popover.Body className="p-2">
                                <div className="d-flex flex-column gap-3" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                    <Button
                                        variant={filters.cortar_no === '' ? "primary" : "outline-secondary"}
                                        size="sm"
                                        onClick={() => {
                                            setTempFilters(prev => ({ ...prev, cortar_no: '' }));
                                            setFilters(prev => ({ ...prev, cortar_no: '' }));
                                            document.body.click();
                                        }}
                                    >
                                        전체 서울
                                    </Button>

                                    {regionData.map(gu => (
                                        <div key={gu.code} className="border rounded p-2 bg-light">
                                            <Button
                                                variant={filters.cortar_no === gu.code ? "primary" : "secondary"}
                                                size="sm"
                                                className="w-100 text-start fw-bold mb-2"
                                                onClick={() => {
                                                    setTempFilters(prev => ({ ...prev, cortar_no: gu.code }));
                                                    setFilters(prev => ({ ...prev, cortar_no: gu.code }));
                                                    document.body.click();
                                                }}
                                            >
                                                {gu.name} (전체)
                                            </Button>
                                            <div className="d-flex flex-wrap gap-1">
                                                {gu.dongs.map(dong => (
                                                    <Button
                                                        key={dong.code}
                                                        variant={filters.cortar_no === dong.code ? "primary" : "white"}
                                                        size="sm"
                                                        className="border shadow-sm"
                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                        onClick={() => {
                                                            setTempFilters(prev => ({ ...prev, cortar_no: dong.code }));
                                                            setFilters(prev => ({ ...prev, cortar_no: dong.code }));
                                                            document.body.click();
                                                        }}
                                                    >
                                                        {dong.name}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Popover.Body>
                        </Popover>
                    }
                >
                    <div className="d-flex align-items-center me-3" style={{ cursor: 'pointer' }}>
                        <h4 className="mb-0 fw-bold me-1">
                            {cortarNoMap[filters.cortar_no] || '전체'}
                        </h4>
                        <FaChevronDown size={14} className="text-muted" />
                    </div>
                </OverlayTrigger>

                {/* 등록일자 필터 */}
                <div className="me-2">
                    <Form.Select
                        size="sm"
                        name="confirm_date"
                        value={filters.confirm_date}
                        onChange={handleFilterChange}
                        className="rounded-pill border-secondary"
                        style={{ width: '130px' }}
                    >
                        {dateOptions.map(date => (
                            <option key={date} value={date}>{date === '' ? '전체' : date}</option>
                        ))}
                    </Form.Select>
                </div>

                {/* 거래유형 필터 */}
                <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    rootClose
                    overlay={
                        <Popover id="popover-trade" style={{ maxWidth: '300px' }}>
                            <Popover.Header as="h3">거래유형</Popover.Header>
                            <Popover.Body>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                    {Object.entries(tradeTypeMap).map(([key, label]) => {
                                        const selected = tempFilters.trade_type.split(',').includes(key);
                                        return (
                                            <Button
                                                key={key}
                                                variant={selected ? "primary" : "outline-secondary"}
                                                size="sm"
                                                className="px-3"
                                                onClick={() => toggleMultiSelect('trade_type', key)}
                                            >
                                                {selected && <FaCheck className="me-1" />} {label}
                                            </Button>
                                        );
                                    })}
                                </div>
                                <Button variant="primary" className="w-100" onClick={applyFilters}>적용</Button>
                            </Popover.Body>
                        </Popover>
                    }
                >
                    <Button variant="outline-secondary" size="sm" className="rounded-pill px-3">
                        {filters.trade_type.split(',').map(t => tradeTypeMap[t] || t).join(', ')}
                    </Button>
                </OverlayTrigger>

                {/* 매물유형 필터 */}
                <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    rootClose
                    overlay={
                        <Popover id="popover-estate" style={{ minWidth: '350px' }}>
                            <Popover.Header as="h3">매물유형</Popover.Header>
                            <Popover.Body>
                                <div className="row g-1 mb-3">
                                    {Object.entries(estateTypeMap).map(([key, label]) => {
                                        const selected = tempFilters.real_estate_type.split(',').includes(key);
                                        return (
                                            <div className="col-4" key={key}>
                                                <Button
                                                    variant={selected ? "primary" : "outline-secondary"}
                                                    size="sm"
                                                    className="w-100 text-truncate p-1"
                                                    style={{ fontSize: '11px' }}
                                                    onClick={() => toggleMultiSelect('real_estate_type', key)}
                                                >
                                                    {selected && <FaCheck size={9} />} {label}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Button variant="primary" className="w-100" onClick={applyFilters}>적용</Button>
                            </Popover.Body>
                        </Popover>
                    }
                >
                    <Button variant="outline-secondary" size="sm" className="rounded-pill px-3 text-truncate" style={{ maxWidth: '150px' }}>
                        {filters.real_estate_type.split(',').map(t => estateTypeMap[t] || t).join(', ')}
                    </Button>
                </OverlayTrigger>

                {/* 가격 필터 (네이버 스타일) */}
                <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    rootClose
                    overlay={
                        <Popover id="popover-price" style={{ minWidth: '320px' }}>
                            <Popover.Header as="h3">매매가</Popover.Header>
                            <Popover.Body>
                                <div className="row g-1 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {pricePresets.map(p => {
                                        const min = parseFloat(tempFilters.min_price || 0);
                                        const max = parseFloat(tempFilters.max_price || Infinity); // Use Infinity for max if not set
                                        const isSelected = (p >= min && p <= max) && (tempFilters.min_price || tempFilters.max_price);
                                        return (
                                            <div className="col-4" key={p}>
                                                <Button
                                                    variant={isSelected ? "primary" : "outline-secondary"}
                                                    size="sm"
                                                    className="w-100"
                                                    onClick={() => handleRangeSelect('price', p.toString())}
                                                >
                                                    {p >= 10000 ? `${p / 10000}억` : `${p / 1000}천`}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="d-flex align-items-center gap-1 mb-3">
                                    <Form.Control size="sm" placeholder="최소" value={tempFilters.min_price} onChange={(e) => handleTempFilterChange('min_price', e.target.value)} />
                                    <span>~</span>
                                    <Form.Control size="sm" placeholder="최대" value={tempFilters.max_price} onChange={(e) => handleTempFilterChange('max_price', e.target.value)} />
                                </div>
                                <div className="d-flex gap-1">
                                    <Button variant="secondary" className="flex-grow-1" onClick={() => resetFilters('price')}>조건삭제</Button>
                                    <Button variant="primary" className="flex-grow-1" onClick={applyFilters}>적용</Button>
                                </div>
                            </Popover.Body>
                        </Popover>
                    }
                >
                    <Button variant="outline-secondary" size="sm" className="rounded-pill px-3">
                        {filters.min_price || filters.max_price ?
                            `${filters.min_price ? (filters.min_price >= 10000 ? filters.min_price / 10000 + '억' : filters.min_price / 1000 + '천') : '0'} ~ ${filters.max_price ? (filters.max_price >= 10000 ? filters.max_price / 10000 + '억' : filters.max_price / 1000 + '천') : '무제한'}`
                            : '가격'
                        }
                    </Button>
                </OverlayTrigger>

                {/* 면적 필터 (네이버 스타일) */}
                <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    rootClose
                    overlay={
                        <Popover id="popover-area" style={{ minWidth: '250px' }}>
                            <Popover.Header as="h3" className="d-flex justify-content-between align-items-center">
                                면적
                                <Button variant="outline-primary" size="sm" style={{ fontSize: '10px' }} onClick={() => setAreaUnit(areaUnit === 'm2' ? 'py' : 'm2')}>
                                    {areaUnit === 'm2' ? '평' : '㎡'}
                                </Button>
                            </Popover.Header>
                            <Popover.Body>
                                <div className="row g-1 mb-3">
                                    {areaPresets.map(a => {
                                        const min = parseFloat(tempFilters.min_area || 0);
                                        const max = parseFloat(tempFilters.max_area || Infinity); // Use Infinity for max if not set
                                        const isSelected = (a >= min && a <= max) && (tempFilters.min_area || tempFilters.max_area);
                                        return (
                                            <div className="col-4" key={a}>
                                                <Button
                                                    variant={isSelected ? "primary" : "outline-secondary"}
                                                    size="sm"
                                                    className="w-100"
                                                    onClick={() => handleRangeSelect('area', a.toString())}
                                                >
                                                    {areaUnit === 'm2' ? `${a}㎡` : m2toPy(a)}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="d-flex gap-1">
                                    <Button variant="secondary" className="flex-grow-1" onClick={() => resetFilters('area')}>조건삭제</Button>
                                    <Button variant="primary" className="flex-grow-1" onClick={applyFilters}>적용</Button>
                                </div>
                            </Popover.Body>
                        </Popover>
                    }
                >
                    <Button variant="outline-secondary" size="sm" className="rounded-pill px-3">
                        {filters.min_area || filters.max_area ?
                            `${filters.min_area || '0'} ~ ${filters.max_area || '무제한'}㎡`
                            : '면적'
                        }
                    </Button>
                </OverlayTrigger>

                <div className="ms-auto d-flex gap-2">
                    <InputGroup size="sm" style={{ width: '150px' }}>
                        <Form.Control
                            placeholder="매물명"
                            name="article_name"
                            value={tempFilters.article_name}
                            onChange={(e) => handleTempFilterChange('article_name', e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                        />
                        <Button variant="outline-secondary" onClick={applyFilters}>
                            <FaSearch />
                        </Button>
                    </InputGroup>
                    <Button variant="outline-danger" size="sm" onClick={() => { setTempFilters({ ...filters, article_name: '', min_price: '', max_price: '', min_area: '', max_area: '' }); applyFilters(); }}>
                        <FaRedo />
                    </Button>
                </div>
            </div >

            {/* 지도 및 로드뷰 영역 */}
            <div className="position-relative mb-4 overflow-hidden rounded shadow-sm border">
                <div id="map" style={{ width: '100%', height: isMobile ? '400px' : '600px', borderRadius: '0' }}></div>

                {/* 정비사업 레이어 토글 버튼 (FAB) */}
                <div className="dev-layer-fab d-flex align-items-center gap-2" onClick={() => setShowDevLayer(!showDevLayer)}>
                    <FaMapMarkerAlt className="text-primary" />
                    <span>정비구역 {activeDevelopments.length > 0 && `(${activeDevelopments.length})`}</span>
                </div>

                {/* 정비사업 안내 레이어 (사이드바/바텀시트) */}
                {renderDevelopmentLayer()}

                {/* 로드뷰 컨테이너 (항상 존재, showRoadview로 표시 여부 결정) */}
                <div
                    id="roadview-wrapper"
                    style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        width: isMobile ? '95%' : '400px',
                        height: isMobile ? '250px' : '300px',
                        zIndex: 10,
                        border: '3px solid white',
                        boxShadow: '0 0 15px rgba(0,0,0,0.5)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        display: showRoadview ? 'block' : 'none'
                    }}
                >
                    <div id="roadview" style={{ width: '100%', height: '100%' }}></div>
                    <Button
                        variant="danger"
                        size="sm"
                        style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 11, borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => setShowRoadview(false)}
                    >
                        X
                    </Button>
                    <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', padding: '5px 10px', background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '12px', zIndex: 11 }}>
                        {selectedEstate?.article_name} 주변 로드뷰
                    </div>
                </div>
            </div>

            {/* 상세 정보 테이블 */}
            {
                selectedEstate && (
                    <div className="selected-detail p-0 border rounded bg-white shadow-sm mb-4 animate__animated animate__fadeIn overflow-hidden">
                        {/* 프리미엄 커스텀 탭 헤더 (PC 드래그 & 네비 버튼) */}
                        <div className="custom-detail-tabs-container m-3 mb-0">
                            <div className="custom-tabs-scroll-wrapper">
                                <Button variant="light" size="sm" className="tab-nav-btn left" onClick={() => scrollTabs('left')}><FaChevronLeft /></Button>
                                <div
                                    className={`custom-tabs-scroll ${isDragging ? 'dragging' : ''}`}
                                    ref={tabScrollRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseLeave={handleMouseLeave}
                                    onMouseUp={handleMouseUp}
                                    onMouseMove={handleMouseMove}
                                >
                                    {krasTabs.map((tab) => (
                                        <div
                                            key={tab.key}
                                            className={`custom-tab-item ${activeDetailTab === tab.key ? 'active' : ''}`}
                                            onClick={() => {
                                                if (!isDragging) {
                                                    setActiveDetailTab(tab.key);
                                                    if (tab.service) fetchKrasData(tab.service);
                                                }
                                            }}
                                        >
                                            {tab.label}
                                        </div>
                                    ))}
                                </div>
                                <Button variant="light" size="sm" className="tab-nav-btn right" onClick={() => scrollTabs('right')}><FaChevronRight /></Button>
                            </div>
                        </div>

                        <div className="p-3">
                            {activeDetailTab === 'detail' && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                        <h5 className="mb-0 d-flex align-items-center">
                                            <FaBuilding className="me-2 text-primary" /> 매물 상세 정보
                                        </h5>
                                    </div>
                                    <Row>
                                        <Col md={4} className="text-center mb-3 mb-md-0">
                                            {selectedEstate.image_url ? (
                                                <img
                                                    src={`https://landthumb-phinf.pstatic.net${selectedEstate.image_url}`}
                                                    alt={selectedEstate.article_name}
                                                    style={{ width: '100%', maxHeight: '250px', objectFit: 'cover', borderRadius: '5px' }}
                                                    onError={(e) => { e.target.src = 'https://placehold.co/300x200?text=No+Image'; }}
                                                />
                                            ) : (
                                                <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: '200px', borderRadius: '5px' }}>
                                                    <FaImages size={50} className="text-muted" />
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={8}>
                                            <Table responsive size="sm" bordered>
                                                <tbody>
                                                    <tr>
                                                        <th className="table-light w-25">매물명</th>
                                                        <td className="d-flex align-items-center gap-2">
                                                            <span className="fw-bold">{selectedEstate.article_name}</span>
                                                            <a
                                                                href={`https://fin.land.naver.com/articles/${selectedEstate.article_no}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ textDecoration: 'none' }}
                                                                title="네이버 부동산 상세보기"
                                                            >
                                                                <img
                                                                    src="/images/cp/naver_land.png"
                                                                    alt="Naver"
                                                                    style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #eee' }}
                                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                                />
                                                            </a>
                                                            {selectedEstate.mobile_article_url && (
                                                                <a
                                                                    href={selectedEstate.mobile_article_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ textDecoration: 'none' }}
                                                                    title={`${selectedEstate.cp_id || '제공사'} 상세보기`}
                                                                >
                                                                    <img
                                                                        src={cpIconMap[(selectedEstate.cp_id || '').toLowerCase()] || '/images/cp/naver_land.png'}
                                                                        alt={selectedEstate.cp_id}
                                                                        style={{ width: '22px', height: '22px', borderRadius: '4px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #eee' }}
                                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                                    />
                                                                </a>
                                                            )}
                                                        </td>
                                                        <th className="table-light w-25">거래유형</th>
                                                        <td className="fw-bold" style={{ color: tradeTypeColorMap[selectedEstate.trade_type] }}>{tradeTypeMap[selectedEstate.trade_type] || selectedEstate.trade_type}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="table-light">가격</th>
                                                        <td className="text-primary fw-bold" style={{ fontSize: '1.2rem' }}>
                                                            {selectedEstate.trade_type === 'B2' && selectedEstate.rent_price
                                                                ? `${selectedEstate.price_text} / ${selectedEstate.rent_price.toLocaleString()}`
                                                                : selectedEstate.price_text}
                                                        </td>
                                                        <th className="table-light">층수</th>
                                                        <td>{selectedEstate.floor_info}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="table-light">면적 (공급/전용)</th>
                                                        <td>
                                                            {selectedEstate.supply_area}㎡{m2toPy(selectedEstate.supply_area)} /
                                                            <span className="fw-bold"> {selectedEstate.exclusive_area}㎡{m2toPy(selectedEstate.exclusive_area)}</span>
                                                        </td>
                                                        <th className="table-light">방향</th>
                                                        <td>{selectedEstate.direction}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="table-light">상세주소</th>
                                                        <td className="fw-bold text-dark">
                                                            <FaMapMarkerAlt className="text-danger me-1" /> {selectedEstate.address || '불러오는 중...'}
                                                        </td>
                                                        <th className="table-light">지도앱</th>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <a
                                                                    href={`https://map.naver.com/v5/search/${encodeURIComponent(selectedEstate.address)}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ textDecoration: 'none' }}
                                                                    title="네이버 지도에서 보기"
                                                                >
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', backgroundColor: '#03C75A', color: 'white', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>N</span>
                                                                </a>
                                                                <a
                                                                    href={`https://map.kakao.com/link/search/${encodeURIComponent(selectedEstate.address)}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{ textDecoration: 'none' }}
                                                                    title="카카오 맵에서 보기"
                                                                >
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', backgroundColor: '#FEE500', color: '#191919', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>K</span>
                                                                </a>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th className="table-light">확인일자</th>
                                                        <td>{selectedEstate.confirm_date}</td>
                                                        <th className="table-light">로드뷰</th>
                                                        <td>
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                className="fw-bold d-flex align-items-center py-1"
                                                                style={{ fontSize: '12px' }}
                                                                onClick={() => toggleRoadview(selectedEstate)}
                                                            >
                                                                <FaStreetView className="me-1" /> 보기
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th className="table-light">특징</th>
                                                        <td colSpan="3">
                                                            {selectedEstate.tags && selectedEstate.tags.map((tag, idx) => (
                                                                <span key={idx} className="badge bg-info text-dark me-1" style={{ fontWeight: 'normal' }}>#{tag}</span>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>

                                    {/* 실거래가 섹션 */}
                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                                            <FaCheck className="text-success me-2" /> 과거 실거래가
                                            {!isTransLoading && transactions.filter(t => t.json && t.json.deal_year).length > 0 &&
                                                ` (최근 ${transactions.filter(t => t.json && t.json.deal_year).length}건)`
                                            }
                                        </h6>

                                        {isTransLoading ? (
                                            <div className="text-center p-3">
                                                <ClipLoader size={25} color="#007bff" />
                                                <div className="text-muted small mt-2">데이터 로딩 중...</div>
                                            </div>
                                        ) : transactions.filter(t => t.json && t.json.deal_year).length > 0 ? (
                                            <>
                                                <Table responsive size="sm" striped bordered hover className="small mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>계약일</th>
                                                            <th>구분</th>
                                                            <th>금액(보증금)</th>
                                                            <th>월세</th>
                                                            <th>면적</th>
                                                            <th>층</th>
                                                            <th>계약기간</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {transactions
                                                            .filter(item => item.json && item.json.deal_year)
                                                            .slice(0, showAllTransactions ? undefined : 5)
                                                            .map((trans, idx) => {
                                                                const t = trans.json;
                                                                const deposit = parseInt(t.deposit_amount || 0);
                                                                const amountText = deposit >= 10000
                                                                    ? `${Math.floor(deposit / 10000)}억 ${deposit % 10000 > 0 ? (deposit % 10000).toLocaleString() : ''}`
                                                                    : deposit.toLocaleString();

                                                                const dealTypeMap = { 'S': '매매', 'J': '전세', 'R': '월세' };
                                                                const dealTypeText = dealTypeMap[t.deal_type] || t.deal_type || '-';

                                                                const mRent = t.monthlyRent !== undefined ? t.monthlyRent : (t.monthly_rent || 0);

                                                                // 매물 층 정보(예: 3/5, B1/3)와 실거래가 층(t.floor) 매칭 확인
                                                                const floorInfo = selectedEstate?.floor_info || '';
                                                                const currentFloorPart = floorInfo.split('/')[0]; // "3" 또는 "B1"
                                                                const transFloorStr = String(t.floor || '');

                                                                // "B1" -> "-1" 또는 "-1" -> "B1" 케이스 대응
                                                                const normalizedTransFloor = transFloorStr.startsWith('-')
                                                                    ? 'B' + transFloorStr.replace('-', '')
                                                                    : transFloorStr;

                                                                const isMatch = currentFloorPart === normalizedTransFloor;

                                                                return (
                                                                    <tr key={idx} className={isMatch ? 'table-primary' : ''}>
                                                                        <td>{t.deal_year || '-'}.{String(t.deal_month || '').padStart(2, '0')}.{String(t.deal_day || '').padStart(2, '0')}</td>
                                                                        <td className="fw-bold" style={{ color: tradeTypeColorMap[t.deal_type] }}>{dealTypeText}</td>
                                                                        <td className="fw-bold text-danger">{amountText}</td>
                                                                        <td className={t.deal_type === 'R' ? 'text-primary fw-bold' : ''}>
                                                                            {t.deal_type === 'R' ? (mRent).toLocaleString() : '-'}
                                                                        </td>
                                                                        <td>{t.exclu_use_ar}㎡{m2toPy(t.exclu_use_ar)}</td>
                                                                        <td>
                                                                            {t.floor}층
                                                                            {isMatch && <span className="badge bg-primary ms-1" style={{ fontSize: '10px' }}>매물층</span>}
                                                                        </td>
                                                                        <td>{t.extra_data?.contractTerm || '-'}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </Table>
                                                {transactions.filter(t => t.json && t.json.deal_year).length > 5 && (
                                                    <div className="d-grid mt-2">
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            className="border text-muted py-2 d-flex align-items-center justify-content-center gap-2"
                                                            onClick={() => setShowAllTransactions(!showAllTransactions)}
                                                        >
                                                            {showAllTransactions ? (
                                                                <>접기 <FaChevronUp /></>
                                                            ) : (
                                                                <>실거래가 {transactions.filter(t => t.json && t.json.deal_year).length - 5}건 더보기 <FaChevronDown /></>
                                                            )}
                                                        </Button>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-center p-4 text-muted small border bg-light rounded">
                                                과거 실거래가 내역이 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}


                            {activeDetailTab === 'tax' && (
                                <div className="tax-calculator-section p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h6 className="fw-bold mb-0 d-flex align-items-center">
                                            <span className="me-2" style={{ fontSize: '1.2rem' }}>💰</span>
                                            {isFirstHome ? '생애 최초 혜택 적용 최종 소요 비용 (예상)' : '매수 시 예상 지출 비용'}
                                        </h6>
                                        <Form.Check
                                            type="checkbox"
                                            id="first-home-check"
                                            label="생애 최초 주택 매수"
                                            checked={isFirstHome}
                                            onChange={(e) => setIsFirstHome(e.target.checked)}
                                            className="fw-bold text-primary"
                                        />
                                    </div>

                                    <div className="table-responsive">
                                        <Table bordered hover size="sm" className="align-middle">
                                            <thead className="table-light">
                                                <tr className="text-center">
                                                    <th style={{ width: '20%' }}>항목</th>
                                                    <th style={{ width: '45%' }}>상세 내역</th>
                                                    <th style={{ width: '35%' }}>금액 (원)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className="text-center fw-bold">1. 매매가</td>
                                                    <td className="small text-muted">실거래가 및 실구매가 기준</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end fw-bold"
                                                            value={taxData.price.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('price', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold">2. 취득세</td>
                                                    <td className="small text-muted">
                                                        {isFirstHome ? '200만원 감면 혜택 적용됨' : '매매가 대비 표준세율 적용'}
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.acqTax.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('acqTax', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">3. 지방교육세</td>
                                                    <td className="small text-muted">취득세의 10% 수준</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.eduTax.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('eduTax', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">4. 농어촌특별세</td>
                                                    <td className="small text-muted">
                                                        {parseFloat(selectedEstate?.exclusive_area) > 85 ? '전용 85㎡ 초과 (과세 대상)' : '전용 85㎡ 이하 (면제)'}
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.ruralTax.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('ruralTax', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">5. 인지세/증지대</td>
                                                    <td className="small text-muted">등기 시 수입인지 비용</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.stampDuty.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('stampDuty', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">6. 국민주택채권</td>
                                                    <td className="small text-muted">채권 할인 및 매입 자부담 (변동성 있음)</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.bondFee.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('bondFee', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-primary">7. 중개수수료</td>
                                                    <td className="small text-muted">상한요율 적용 (부가세 10% 포함)</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.brokerFee.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('brokerFee', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">8. 법무사 수수료</td>
                                                    <td className="small text-muted">대행료 및 기타 실비</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.legalFee.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('legalFee', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className="text-center fw-bold text-muted">9. 이사 비용</td>
                                                    <td className="small text-muted">사용자 입력값 (초기 150만원)</td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            className="text-end"
                                                            value={taxData.movingFee.toLocaleString()}
                                                            onChange={(e) => handleTaxInputChange('movingFee', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr className="table-dark">
                                                    <td colSpan="2" className="text-center fw-bold">합계 (예상)</td>
                                                    <td className="text-end fw-bold" style={{ fontSize: '1.2rem', color: '#ffeb3b' }}>
                                                        {(Object.values(taxData).reduce((a, b) => a + b, 0) - taxData.price).toLocaleString()}원
                                                        <br />
                                                        <span className="small" style={{ color: '#fff', fontSize: '0.8rem' }}>
                                                            (총 소요 예산: {Object.values(taxData).reduce((a, b) => a + b, 0).toLocaleString()}원)
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </div>
                                    <p className="mt-2 text-muted" style={{ fontSize: '11px' }}>
                                        * 위 계산은 실제와 차이가 있을 수 있으며, 참고용으로만 사용하시기 바랍니다. 채권 매입가 등은 일자에 따라 변동될 수 있습니다.
                                    </p>
                                </div>
                            )}

                            {activeDetailTab === 'title' && (
                                <div className="kras-title-section">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                                        <FaBuilding className="text-primary me-2" /> 표제부
                                    </h6>
                                    {isKrasLoading.title ? (
                                        <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                                    ) : krasData.title && krasData.title.length > 0 ? (
                                        <div className="kras-table-wrapper">
                                            {krasData.title.map((item, idx) => (
                                                <Table responsive bordered size="sm" key={idx} className="small mb-4 kras-table">
                                                    <tbody>
                                                        <tr>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>대지위치</th>
                                                            <td colSpan="3">{item.platPlc}</td>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>지번</th>
                                                            <td style={{ width: '15%' }}>{item.bun}-{item.ji}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">대지면적</th>
                                                            <td style={{ width: '18%' }}>{item.platArea}㎡{m2toPy(item.platArea)}</td>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>연면적</th>
                                                            <td style={{ width: '18%' }}>{item.totArea}㎡{m2toPy(item.totArea)}</td>
                                                            <th className="table-light text-center">명칭 및 번호</th>
                                                            <td>{item.bldNm || '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">건축면적</th>
                                                            <td>{item.archArea}㎡{m2toPy(item.archArea)}</td>
                                                            <th className="table-light text-center">용적률 산정용 연면적</th>
                                                            <td>{item.vlRatEstmTotArea}㎡{m2toPy(item.vlRatEstmTotArea)}</td>
                                                            <th className="table-light text-center">건축물수</th>
                                                            <td>{item.mainAtchGbCdNm}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">건폐율</th>
                                                            <td>{item.bcRat}%</td>
                                                            <th className="table-light text-center">용적률</th>
                                                            <td>{item.vlRat}%</td>
                                                            <th className="table-light text-center">총호수</th>
                                                            <td>{item.hhldCnt}세대 / {item.fmlyCnt}가구</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">주용도</th>
                                                            <td>{item.mainPurpsCdNm}</td>
                                                            <th className="table-light text-center">주구조</th>
                                                            <td>{item.strctCdNm}</td>
                                                            <th className="table-light text-center">부속건축물</th>
                                                            <td>{item.atchBldCnt}동 / {item.atchBldArea}㎡{m2toPy(item.atchBldArea)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">허가일자</th>
                                                            <td>{item.pmsDay ? `${item.pmsDay.substring(0, 4)}.${item.pmsDay.substring(4, 6)}.${item.pmsDay.substring(6, 8)}` : '-'}</td>
                                                            <th className="table-light text-center">착공일자</th>
                                                            <td>{item.stcnsDay ? `${item.stcnsDay.substring(0, 4)}.${item.stcnsDay.substring(4, 6)}.${item.stcnsDay.substring(6, 8)}` : '-'}</td>
                                                            <th className="table-light text-center">사용승인일자</th>
                                                            <td>{item.useAprDay ? `${item.useAprDay.substring(0, 4)}.${item.useAprDay.substring(4, 6)}.${item.useAprDay.substring(6, 8)}` : '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">위반건축물 여부</th>
                                                            <td className={item.rserthqkDsgnApplyYn === '1' ? 'fw-bold text-danger' : ''}>{item.rserthqkDsgnApplyYn === '1' ? '해당' : '해당없음'}</td>
                                                            <th className="table-light text-center">특이사항</th>
                                                            <td colSpan="3">{item.etcPurps || '-'}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="alert alert-light text-center p-4 border small">조회된 데이터가 없습니다.</div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === 'recap' && (
                                <div className="kras-recap-section">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                                        <FaBuilding className="text-primary me-2" /> 총괄표제부
                                    </h6>
                                    {isKrasLoading.recap ? (
                                        <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                                    ) : krasData.recap && krasData.recap.length > 0 ? (
                                        <div className="kras-table-wrapper">
                                            {krasData.recap.map((item, idx) => (
                                                <Table responsive bordered size="sm" key={idx} className="small mb-4 kras-table">
                                                    <tbody>
                                                        <tr>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>대지위치</th>
                                                            <td colSpan="3">{item.platPlc}</td>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>지번</th>
                                                            <td style={{ width: '15%' }}>{item.bun}-{item.ji}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">대지면적</th>
                                                            <td style={{ width: '18%' }}>{item.platArea}㎡{m2toPy(item.platArea)}</td>
                                                            <th className="table-light text-center" style={{ width: '15%' }}>연면적</th>
                                                            <td style={{ width: '18%' }}>{item.totArea}㎡{m2toPy(item.totArea)}</td>
                                                            <th className="table-light text-center">명칭 및 번호</th>
                                                            <td>{item.bldNm || '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">건축면적</th>
                                                            <td>{item.archArea}㎡{m2toPy(item.archArea)}</td>
                                                            <th className="table-light text-center">용적률 산정용 연면적</th>
                                                            <td>{item.vlRatEstmTotArea}㎡{m2toPy(item.vlRatEstmTotArea)}</td>
                                                            <th className="table-light text-center">건물동수</th>
                                                            <td>{item.mainAtchGbCdNm}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">건폐율</th>
                                                            <td>{item.bcRat}%</td>
                                                            <th className="table-light text-center">용적률</th>
                                                            <td>{item.vlRat}%</td>
                                                            <th className="table-light text-center">총호수</th>
                                                            <td>{item.hhldCnt}세대 / {item.fmlyCnt}가구</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">주용도</th>
                                                            <td>{item.mainPurpsCdNm}</td>
                                                            <th className="table-light text-center">주구조</th>
                                                            <td>{item.strctCdNm}</td>
                                                            <th className="table-light text-center">부속건축물</th>
                                                            <td>{item.atchBldCnt}동 / {item.atchBldArea}㎡{m2toPy(item.atchBldArea)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">허가일자</th>
                                                            <td>{item.pmsDay ? `${item.pmsDay.substring(0, 4)}.${item.pmsDay.substring(4, 6)}.${item.pmsDay.substring(6, 8)}` : '-'}</td>
                                                            <th className="table-light text-center">착공일자</th>
                                                            <td>{item.stcnsDay ? `${item.stcnsDay.substring(0, 4)}.${item.stcnsDay.substring(4, 6)}.${item.stcnsDay.substring(6, 8)}` : '-'}</td>
                                                            <th className="table-light text-center">사용승인일자</th>
                                                            <td>{item.useAprDay ? `${item.useAprDay.substring(0, 4)}.${item.useAprDay.substring(4, 6)}.${item.useAprDay.substring(6, 8)}` : '-'}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="table-light text-center">위반건축물 여부</th>
                                                            <td className={item.rserthqkDsgnApplyYn === '1' ? 'fw-bold text-danger' : ''}>{item.rserthqkDsgnApplyYn === '1' ? '해당' : '해당없음'}</td>
                                                            <th className="table-light text-center">특이사항</th>
                                                            <td colSpan="3">{item.etcPurps || '-'}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="alert alert-light text-center p-4 border small">조회된 데이터가 없습니다.</div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === 'floor' && (
                                <div className="kras-floor-section">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center">
                                        <FaBuilding className="text-primary me-2" /> 층별현황
                                    </h6>
                                    {isKrasLoading.floor ? (
                                        <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>
                                    ) : krasData.floor && krasData.floor.length > 0 ? (
                                        <Table responsive striped bordered hover size="sm" className="small kras-table">
                                            <thead className="table-light text-center">
                                                <tr>
                                                    <th style={{ width: '15%' }}>구분</th>
                                                    <th style={{ width: '15%' }}>층별</th>
                                                    <th style={{ width: '30%' }}>구조</th>
                                                    <th style={{ width: '25%' }}>용도</th>
                                                    <th style={{ width: '15%' }}>면적(㎡)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-center">
                                                {[...krasData.floor].sort((a, b) => {
                                                    const order = { '지하': -1, '지상': 1, '옥탑': 2, '옥외': 3 };
                                                    const aOrder = order[a.flrGbCdNm] || 0;
                                                    const bOrder = order[b.flrGbCdNm] || 0;
                                                    if (aOrder !== bOrder) return aOrder - bOrder;
                                                    return a.flrNo - b.flrNo;
                                                }).map((item, idx) => {
                                                    // 매물 층 정보(예: 2/5, B1/3)와 현재 행의 층(item.flrNoNm) 매칭 확인
                                                    const floorInfo = selectedEstate?.floor_info || '';
                                                    const currentFloorPart = floorInfo.split('/')[0]; // "2" 또는 "B1"

                                                    let isMatch = false;
                                                    if (currentFloorPart) {
                                                        // "2층" -> "2", "지하1층" -> "B1" 등으로 정규화하여 비교
                                                        const normalizedFlr = item.flrNoNm.replace('층', '');
                                                        const compareStr = normalizedFlr.startsWith('지하')
                                                            ? 'B' + normalizedFlr.replace('지하', '')
                                                            : normalizedFlr;

                                                        isMatch = currentFloorPart === compareStr;
                                                    }

                                                    return (
                                                        <tr key={idx} className={isMatch ? 'table-primary border-primary' : ''} style={isMatch ? { borderLeft: '4px solid #0d6efd' } : {}}>
                                                            <td>{item.flrGbCdNm}</td>
                                                            <td className="fw-bold">{item.flrNoNm} {isMatch && <span className="badge bg-primary ms-1">매물</span>}</td>
                                                            <td>{item.strctCdNm}</td>
                                                            <td>{item.etcPurps || item.mainPurpsCdNm}</td>
                                                            <td className="text-end px-3">{(item.area || 0).toLocaleString()}㎡{m2toPy(item.area)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="alert alert-light text-center p-4 border small">조회된 데이터가 없습니다.</div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === 'basis' && renderKrasCommonData('basis', '기본개요')}
                            {activeDetailTab === 'exposArea' && renderKrasCommonData('exposArea', '전유공용면적')}
                            {activeDetailTab === 'hsprc' && renderKrasCommonData('hsprc', '주택가격')}
                            {activeDetailTab === 'expos' && renderKrasCommonData('expos', '전유부')}
                            {activeDetailTab === 'wclf' && renderKrasCommonData('wclf', '오수정화시설')}
                            {activeDetailTab === 'atchJibun' && renderKrasCommonData('atchJibun', '부속지번')}
                            {activeDetailTab === 'jijigu' && renderKrasCommonData('jijigu', '지역지구구역')}
                        </div>
                    </div>
                )
            }

            {/* 전체 목록 테이블 */}
            <div className="list-section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 d-flex align-items-center">
                        <FaBuilding className="me-2" /> 매물 목록 ({estateList.length}건)
                    </h5>
                </div>
                <div className="land-table-wrapper">
                    <Table responsive striped bordered hover size="sm" className="small">
                        <thead className="table-dark">
                            <tr>
                                <th onClick={() => handleSort('cortar_no')} style={{ cursor: 'pointer' }}>법정동 {getSortIcon('cortar_no')}</th>
                                <th onClick={() => handleSort('article_name')} style={{ cursor: 'pointer' }}>매물명 {getSortIcon('article_name')}</th>
                                <th onClick={() => handleSort('real_estate_type')} style={{ cursor: 'pointer' }}>유형 {getSortIcon('real_estate_type')}</th>
                                <th onClick={() => handleSort('trade_type')} style={{ cursor: 'pointer' }}>거래 {getSortIcon('trade_type')}</th>
                                <th onClick={() => handleSort('price_text')} style={{ cursor: 'pointer' }}>가격 {getSortIcon('price_text')}</th>
                                <th onClick={() => handleSort('exclusive_area')} style={{ cursor: 'pointer' }}>면적(전용) {getSortIcon('exclusive_area')}</th>
                                <th onClick={() => handleSort('floor_info')} style={{ cursor: 'pointer' }}>층 {getSortIcon('floor_info')}</th>
                                <th onClick={() => handleSort('confirm_date')} style={{ cursor: 'pointer' }}>확인일 {getSortIcon('confirm_date')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedEstateList.length > 0 ? sortedEstateList.map((item) => (
                                <EstateRow
                                    key={item.article_no}
                                    item={item}
                                    isSelected={selectedEstate?.article_no === item.article_no}
                                    onSelect={handleEstateSelect}
                                    cortarNoMap={cortarNoMap}
                                    estateTypeMap={estateTypeMap}
                                    tradeTypeMap={tradeTypeMap}
                                    m2toPy={m2toPy}
                                />
                            )) : (
                                <tr>
                                    <td colSpan="8" className="text-center p-5 text-secondary">
                                        <div className="mb-2" style={{ fontSize: '16px', fontWeight: 'bold' }}>조회된 매물 데이터가 없습니다.</div>
                                        <div style={{ fontSize: '14px' }}>
                                            매물 정보는 매일 <span className="text-primary fw-bold">09시, 13시, 17시, 20시</span>에 업데이트됩니다.<br />
                                            업데이트 시간 이후에 다시 조회를 시도하거나 조회 날짜를 변경해 주세요.
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* 동일 좌표 매물 목록 모달 */}
            <Modal show={activeGroup !== null} onHide={() => setActiveGroup(null)} centered scrollable size="md">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold fs-6">
                        <FaMapMarkerAlt className="text-danger me-2" />
                        해당 위치 매물 목록 ({activeGroup?.length}건)
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="list-group list-group-flush">
                        {activeGroup?.map((item) => (
                            <div
                                key={item.article_no}
                                className="list-group-item list-group-item-action p-3"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    handleEstateSelect(item);
                                    setActiveGroup(null);
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                    <span className="badge" style={{ backgroundColor: tradeTypeColorMap[item.trade_type] || '#666' }}>
                                        {tradeTypeMap[item.trade_type] || item.trade_type}
                                    </span>
                                    <span className="text-muted small">{item.confirm_date}</span>
                                </div>
                                <h6 className="fw-bold text-primary mb-1">
                                    {item.trade_type === 'B2' && item.rent_price
                                        ? `${item.price_text} / ${item.rent_price.toLocaleString()}`
                                        : item.price_text}
                                </h6>
                                <div className="small text-dark">
                                    {estateTypeMap[item.real_estate_type] || item.real_estate_type} | {item.article_name} | {item.exclusive_area}㎡{m2toPy(item.exclusive_area)}
                                </div>
                                <div className="small text-muted mt-1">{item.floor_info}층 | {item.direction}향</div>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light p-2">
                    <Button variant="secondary" size="sm" onClick={() => setActiveGroup(null)}>닫기</Button>
                </Modal.Footer>
            </Modal>

            {
                loading && (
                    <div className="loading-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.3)', zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <ClipLoader color="#ffffff" size={50} />
                        <p className="text-white mt-2">매물 정보를 불러오는 중...</p>
                    </div>
                )
            }
        </Container >
    );
};

export { Land };
